/**
 * The web ticket — the canonical customer surface.
 *
 * WHY THIS AND NOT THE EMAIL. A QR pasted into an inbox is a photograph of a ticket taken
 * at the moment of purchase. It cannot know the ticket was later refunded, transferred or
 * already scanned. This page reads the authoritative order every time it opens, so what it
 * shows is what the door will decide.
 *
 * IT DECIDES NOTHING ITSELF. Status, QR and tier all come from the engine. There is no
 * validity logic here and there must never be: the scanner asks the backend, and so does
 * this page. If they ever disagreed, the backend would be right.
 *
 * Built mobile-first because it is read at a door, one-handed, in the dark.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  getOrderStatus, resendOrderConfirmation,
  type OrderStatus, type OrderTicket,
} from '../../lib/catalog';
import { rememberOrderToken, recallOrderToken } from '../../lib/orderTokens';
import { ApiError } from '../../lib/apiClient';
import { formatDate, formatTime } from '../../lib/format';
import { useCopy } from '../../copy/CopyProvider';
import './TicketsPage.css';

/**
 * What each ticket state means to the person holding it.
 *
 * Status is never communicated by colour alone: every state carries a word, and the
 * unusable ones say why in a sentence.
 */
const STATE: Record<string, { label: string; tone: 'ready' | 'used' | 'void'; note?: string }> = {
  active: { label: 'Ready to scan', tone: 'ready' },
  scanned: { label: 'Already checked in', tone: 'used', note: 'This ticket has been used.' },
  cancelled: { label: 'Cancelled', tone: 'void', note: 'This ticket is no longer valid.' },
  transferred: { label: 'Transferred', tone: 'void', note: 'This ticket was sent to someone else.' },
  pending_payment: { label: 'Awaiting payment', tone: 'void', note: 'This ticket is not active yet.' },
};

const stateOf = (s: string) => STATE[s] ?? { label: s, tone: 'void' as const };

function TicketCard({ ticket, order, index, total }: {
  ticket: OrderTicket; order: OrderStatus; index: number; total: number;
}) {
  const state = stateOf(ticket.status);
  const ev = order.event;

  return (
    <article className="tkt" aria-label={`Ticket ${index + 1} of ${total}`}>
      <header className="tkt__head">
        {ev.imageUrl ? (
          // alt is empty on purpose: the event title is the adjacent heading, so
          // describing the artwork again would just repeat it to a screen reader.
          <img className="tkt__art" src={ev.imageUrl} alt="" loading="lazy" />
        ) : (
          <div className="tkt__art tkt__art--fallback" aria-hidden="true">
            <span>VOLETO</span>
          </div>
        )}
        <div className="tkt__headtext">
          <p className="tkt__count">Ticket {index + 1} of {total}</p>
          <h2 className="tkt__title">{ev.title}</h2>
        </div>
      </header>

      <dl className="tkt__facts">
        <div><dt>Date</dt><dd>{ev.date ? formatDate(ev.date) : 'To be announced'}</dd></div>
        <div>
          <dt>{ev.doorsTime ? 'Doors / Start' : 'Start'}</dt>
          <dd>
            {ev.doorsTime ? `${formatTime(ev.doorsTime)} / ` : ''}
            {ev.time ? formatTime(ev.time) : '—'}
          </dd>
        </div>
        <div>
          <dt>Venue</dt>
          <dd>
            {ev.venueName ?? ev.venue}
            {ev.venueAddress ? <span className="tkt__sub">{ev.venueAddress}</span> : null}
          </dd>
        </div>
        <div><dt>Admit</dt><dd>{ticket.tierName ?? 'General'}</dd></div>
        {order.buyerName ? <div><dt>Name</dt><dd>{order.buyerName}</dd></div> : null}
      </dl>

      <div className={`tkt__qr tkt__qr--${state.tone}`}>
        {ticket.qr ? (
          <>
            <img
              className="tkt__qrimg"
              src={ticket.qr}
              alt={`Scannable QR code for ticket ${index + 1} of ${total}`}
            />
            <p className="tkt__hint">Show this at the door. It scans once.</p>
          </>
        ) : (
          <div className="tkt__noqr" role="status">
            <p className="tkt__noqrlabel">{state.label}</p>
            {state.note ? <p className="tkt__hint">{state.note}</p> : null}
          </div>
        )}
      </div>

      <footer className="tkt__foot">
        <span className={`tkt__state tkt__state--${state.tone}`}>
          {/* A dot AND a word: colour alone never carries the meaning. */}
          <span className="tkt__dot" aria-hidden="true" />
          {state.label}
        </span>
        <span className="tkt__ref mono" title="Ticket reference">
          {ticket.ticketUid.slice(0, 8).toUpperCase()}
        </span>
      </footer>
    </article>
  );
}

export function TicketsPage() {
  const { orderId } = useParams<{ orderId: string }>();
  // Platform-scoped: this page is reached with an order token and does not know the
  // event's organization until the order loads, and re-fetching copy after that would
  // repaint wording the buyer had already read.
  const t = useCopy();
  const [params, setParams] = useSearchParams();

  const [order, setOrder] = useState<OrderStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState<string | null>(null);

  /**
   * THE TOKEN ARRIVES IN THE URL AND DOES NOT STAY THERE.
   *
   * An email link cannot send a header, so the confirmation puts the token in the query
   * string. orderTokens.ts is explicit that a token in a URL leaks into history,
   * bookmarks and referrers, so the moment it is read it is moved into sessionStorage and
   * stripped from the address bar with a replace, leaving nothing to copy or share.
   */
  const tokenFor = useCallback((id: string) => {
    const fromUrl = params.get('token');
    if (fromUrl) {
      rememberOrderToken(id, fromUrl);
      const next = new URLSearchParams(params);
      next.delete('token');
      setParams(next, { replace: true });
      return fromUrl;
    }
    return recallOrderToken(id);
  }, [params, setParams]);

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    const token = tokenFor(orderId);

    (async () => {
      try {
        const result = await getOrderStatus(orderId, token);
        if (!cancelled) setOrder(result);
      } catch (err) {
        if (cancelled) return;
        // A missing order and an unauthorised one answer identically, so the message must
        // not distinguish them either.
        setError(
          err instanceof ApiError && err.status === 404
            ? 'We could not find these tickets. Open the most recent link from your confirmation email.'
            : 'Something went wrong loading your tickets.',
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
    // tokenFor is intentionally excluded: it changes identity when the URL is rewritten,
    // and re-running would refetch immediately after stripping the token.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  async function handleResend() {
    if (!orderId || resending) return;
    setResending(true);
    setError(null);
    try {
      const result = await resendOrderConfirmation(orderId, recallOrderToken(orderId));
      // The resend rotates the token. Storing it is not optional: the previous one is
      // already dead, and a second press would fail without this.
      rememberOrderToken(orderId, result.accessToken);
      setResent(result.sentTo);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 429
          ? 'Too many requests. Please wait a few minutes before trying again.'
          : 'We could not resend that email. Please try again shortly.',
      );
    } finally {
      setResending(false);
    }
  }

  if (loading) return <p className="tkts__status" role="status">Loading your tickets…</p>;

  if (error && !order) {
    return (
      <div className="tkts__status tkts__status--error">
        <p role="alert">{error}</p>
        <Link to="/events" className="button button--secondary">Browse events</Link>
      </div>
    );
  }

  if (!order) return null;

  const tickets = order.tickets;
  const usable = tickets.filter((t) => t.status === 'active').length;

  return (
    <div className="tkts">
      <header className="tkts__top">
        <p className="tkts__eyebrow">{t('tickets.page.eyebrow', 'Your tickets')}</p>
        <h1 className="tkts__title">{order.event.title}</h1>
        <p className="tkts__meta">
          {tickets.length} ticket{tickets.length === 1 ? '' : 's'}
          {usable !== tickets.length ? ` · ${usable} ready to scan` : ''}
          {order.orderReference ? ` · Order ${order.orderReference}` : ''}
        </p>
      </header>

      {tickets.length === 0 ? (
        <p className="tkts__status">{t('tickets.page.empty', 'This order has no tickets.')}</p>
      ) : (
        <div className="tkts__list">
          {tickets.map((t, i) => (
            <TicketCard key={t.ticketId} ticket={t} order={order} index={i} total={tickets.length} />
          ))}
        </div>
      )}

      <section className="tkts__actions">
        {resent ? (
          <p className="tkts__sent" role="status">
            {t('tickets.resend.sent', 'Sent to {email}. Check your inbox.', { email: resent })}
          </p>
        ) : (
          <button
            type="button"
            className="button button--secondary"
            onClick={handleResend}
            disabled={resending}
          >
            {resending ? 'Sending…' : t('tickets.resend.cta', 'Email these tickets to me again')}
          </button>
        )}
        {error ? <p className="tkts__error" role="alert">{error}</p> : null}
        <p className="tkts__note">
          Tickets are sent to the address on the order. Screenshots work, but this page
          always shows the current status of each ticket.
        </p>
        <Link to="/events" className="tkts__back">Browse more events</Link>
      </section>
    </div>
  );
}
